import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  getConnectorStatus,
  type ConnectorPart,
  type ConnectorStatus,
  type ConnectorStatusValue,
} from "../../../api/connectors";

const REFRESH_MS = 15_000;

// status → MUI palette color + label
const STATUS_META: Record<
  ConnectorStatusValue,
  { color: "success" | "warning" | "error"; label: string }
> = {
  online: { color: "success", label: "Online" },
  degraded: { color: "warning", label: "Degraded" },
  offline: { color: "error", label: "Offline" },
};

/** "12s ago" / "3m ago" / "5h ago" / "2d ago" from a seconds count. */
function humanizeAgo(seconds: number | null): string {
  if (seconds == null) return "never";
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/** Format a Nashville-local naive ISO string as a readable local time. */
function formatLocal(iso: string | null): string {
  if (!iso) return "—";
  // Value is already Nashville-local wall-clock; render its fields directly.
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/** "every 30s" / "every 2m 30s" */
function humanizeInterval(seconds: number | null): string {
  if (seconds == null) return "unknown";
  const s = Math.round(seconds);
  if (s < 60) return `every ${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem ? `every ${m}m ${rem}s` : `every ${m}m`;
}

const KeyValueRow: React.FC<{ k: string; v: unknown }> = ({ k, v }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="baseline"
    spacing={2}
  >
    <Typography variant="caption" color="text.secondary">
      {k}
    </Typography>
    <Typography
      variant="caption"
      sx={{ fontWeight: 600, textAlign: "right", wordBreak: "break-word" }}
    >
      {v === null || v === undefined ? "—" : String(v)}
    </Typography>
  </Stack>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="overline"
    color="text.secondary"
    sx={{ display: "block", lineHeight: 1.6 }}
  >
    {children}
  </Typography>
);

/**
 * One PLC behind a connector. The dot carries the status and the text repeats
 * it, since a color alone is not a status — and "last read 3m ago" is the
 * thing that actually tells you whether this PLC is answering.
 */
const PartRow: React.FC<{ p: ConnectorPart }> = ({ p }) => {
  const meta = STATUS_META[p.status] ?? STATUS_META.offline;
  const [open, setOpen] = useState(false);
  const items = p.last_data?.items ?? [];

  return (
    <Box sx={{ borderTop: 1, borderColor: "divider" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        onClick={() => setOpen((v) => !v)}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        sx={{
          py: 0.6,
          cursor: "pointer",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          {open ? (
            <ExpandLessIcon sx={{ fontSize: 16, color: "text.disabled" }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16, color: "text.disabled" }} />
          )}
          <FiberManualRecordIcon color={meta.color} sx={{ fontSize: 11 }} />
          <Typography variant="body2" noWrap>
            {p.label}
            {p.port != null && (
              <Typography component="span" variant="caption" color="text.secondary">
                {" "}
                ({p.port})
              </Typography>
            )}
          </Typography>
        </Stack>
        <Tooltip
          title={
            p.error
              ? `${meta.label} — ${p.error}`
              : p.last_ok_at
              ? `${meta.label} — last read ${formatLocal(p.last_ok_at)}`
              : `${meta.label} — never read`
          }
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap" }}
          >
            {p.reading ? "reading" : meta.label.toLowerCase()} ·{" "}
            {humanizeAgo(p.seconds_since_ok)}
          </Typography>
        </Tooltip>
      </Stack>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ pl: 3.5, pb: 1 }}>
          {/* When this PLC last produced a row, which is not the same as when
              it was last read: unchanged readings are not written. */}
          <SectionLabel>
            Last data
            {p.last_data?.captured_at
              ? ` · ${formatLocal(p.last_data.captured_at)}`
              : ""}
          </SectionLabel>
          {items.length > 0 ? (
            <Stack spacing={0.25} sx={{ mt: 0.25 }}>
              {items.map((it) => (
                <KeyValueRow key={it.label} k={it.label} v={it.value} />
              ))}
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No rows written by this PLC yet.
            </Typography>
          )}
          {p.error && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: "block", mt: 0.75 }}
            >
              Last error: {p.error}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const ConnectorCard: React.FC<{ c: ConnectorStatus }> = ({ c }) => {
  const meta = STATUS_META[c.status] ?? STATUS_META.offline;
  const detailEntries = c.detail ? Object.entries(c.detail) : [];
  const snapshotItems = c.snapshot?.items ?? [];
  // Collapsed by default: the card should answer "is this healthy?" at a
  // glance, with the reference material a click away.
  const [open, setOpen] = useState(false);
  const healthy = c.parts.filter((p) => p.status === "online").length;

  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <FiberManualRecordIcon color={meta.color} sx={{ fontSize: 14 }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {c.name}
            </Typography>
          </Stack>
          <Chip size="small" color={meta.color} label={meta.label} />
        </Stack>

        <Typography variant="caption" color="text.secondary">
          {c.worker} · writes <code>{c.table}</code>
        </Typography>

        {/* Per-PLC health. This is the point of the card for a worker that
            polls several: one live PLC used to keep the whole thing green. */}
        {c.parts.length > 0 && (
          <>
            <Divider sx={{ my: 1.25 }} />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 0.25 }}
            >
              <SectionLabel>PLCs</SectionLabel>
              <Typography variant="caption" color="text.secondary">
                {healthy}/{c.parts.length} healthy
              </Typography>
            </Stack>
            {c.parts.map((p) => (
              <PartRow key={p.key} p={p} />
            ))}
          </>
        )}

        <Divider sx={{ my: 1.25 }} />

        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Last heartbeat
          </Typography>
          <Tooltip title={formatLocal(c.last_beat_at)}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {humanizeAgo(c.seconds_since_beat)}
            </Typography>
          </Tooltip>
        </Stack>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Last row written
          </Typography>
          <Tooltip title={formatLocal(c.last_data_at)}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {humanizeAgo(c.seconds_since_data)}
            </Typography>
          </Tooltip>
        </Stack>

        <Button
          size="small"
          onClick={() => setOpen((v) => !v)}
          endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ mt: 1, px: 0.5 }}
          aria-expanded={open}
        >
          {open ? "Less" : "Details"}
        </Button>

        <Collapse in={open} unmountOnExit>
          <Typography variant="caption" color="text.secondary">
            {c.protocol}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {c.description}
          </Typography>

          {c.provides.length > 0 && (
            <Stack
              direction="row"
              spacing={0.5}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 1.25 }}
            >
              {c.provides.map((p) => (
                <Chip key={p} label={p} size="small" variant="outlined" />
              ))}
            </Stack>
          )}

          <Divider sx={{ my: 1.5 }} />

          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Poll frequency
            </Typography>
            <Tooltip
              title={
                c.poll_seconds_source === "worker"
                  ? "Reported by the running worker"
                  : "Configured default — this worker does not report its interval yet"
              }
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {humanizeInterval(c.poll_seconds)}
                {c.poll_seconds_source === "expected" && " (expected)"}
              </Typography>
            </Tooltip>
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          {/* Only for single-PLC connectors: where there is a per-PLC
              breakdown above, each row already carries its own last reading and
              repeating the aggregate here just shows the same numbers twice. */}
          {c.parts.length === 0 && (
            <>
              <SectionLabel>
                Latest data pull
                {c.snapshot?.captured_at
                  ? ` · ${formatLocal(c.snapshot.captured_at)}`
                  : ""}
              </SectionLabel>
              {snapshotItems.length > 0 ? (
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  {snapshotItems.map((it) => (
                    <KeyValueRow key={it.label} k={it.label} v={it.value} />
                  ))}
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No rows in {c.table} yet.
                </Typography>
              )}
            </>
          )}

          {detailEntries.length > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <SectionLabel>Last worker cycle</SectionLabel>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {detailEntries.map(([k, v]) => (
                  <KeyValueRow key={k} k={k} v={v} />
                ))}
              </Stack>
            </>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

const ConnectorsPage: React.FC = () => {
  const [rows, setRows] = useState<ConnectorStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    document.title = "Connectors";
  }, []);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const data = await getConnectorStatus(ctrl.signal);
      setRows(data);
      setError(null);
      setUpdatedAt(new Date());
    } catch (e: any) {
      if (e?.name !== "CanceledError" && e?.code !== "ERR_CANCELED") {
        setError("Failed to load connector status.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => {
      clearInterval(id);
      abortRef.current?.abort();
    };
  }, [load]);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Connectors
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Live status of every PLC data connector.
            {updatedAt && ` Updated ${updatedAt.toLocaleTimeString()}.`}
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={load} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {loading && rows.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {rows.map((c) => (
            <Grid key={c.key} item xs={12} md={6}>
              <ConnectorCard c={c} />
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default ConnectorsPage;
